import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForgotPassword } from '../../hooks/auth/usePasswordReset';
import { NotificationBanner } from '../../components/NotificationBanner';
import { Mail, Loader2, CheckCircle, ArrowLeft } from 'lucide-react';

export const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const { mutate: forgotPassword, isLoading, isSuccess, error } = useForgotPassword();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      return;
    }

    if (!email.includes('@')) {
      return;
    }

    forgotPassword(email);
  };

  return (
    <div className="tw-min-h-screen tw-flex tw-items-center tw-justify-center tw-bg-gradient-to-br tw-from-affinicar-secondary tw-to-affinicar-dark tw-p-4">
      <div className="tw-w-full tw-max-w-md">
        {/* Card */}
        <div className="tw-bg-white tw-rounded-2xl tw-shadow-2xl tw-overflow-hidden">
          {/* Header */}
          <div className="tw-bg-gradient-to-r tw-from-affinicar-primary tw-to-affinicar-secondary tw-p-8 tw-text-center">
            <div className="tw-mx-auto tw-mb-4">
              <img
                src="/assets/images/affinicar/logo_full_white.png"
                alt="Affinicar"
                className="tw-h-16 tw-w-auto tw-mx-auto"
              />
            </div>
            <h1 className="tw-text-2xl tw-font-bold tw-text-white tw-mb-2">
              Mot de passe oublié
            </h1>
            <p className="tw-text-white tw-text-opacity-90">
              Entrez votre adresse email pour recevoir un lien de réinitialisation
            </p>
          </div>

          {/* Content */}
          <div className="tw-p-8">
            {!!error && (
              <div className="tw-mb-6">
                <NotificationBanner
                  message={(error as any)?.response?.data?.message || 'Une erreur est survenue'}
                  type="error"
                />
              </div>
            )}

            {isSuccess ? (
              <div className="tw-space-y-6">
                <div className="tw-flex tw-flex-col tw-items-center tw-text-center">
                  <div className="tw-w-16 tw-h-16 tw-bg-green-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mb-4">
                    <CheckCircle className="tw-w-8 tw-h-8 tw-text-green-600" />
                  </div>
                  <h2 className="tw-text-xl tw-font-semibold tw-text-gray-900 tw-mb-2">
                    Email envoyé
                  </h2>
                  <p className="tw-text-gray-600 tw-mb-2">
                    Si un compte existe avec cet email, vous recevrez un lien de réinitialisation dans quelques instants.
                  </p>
                  <p className="tw-text-sm tw-text-gray-500">
                    Pensez à vérifier votre dossier spam.
                  </p>
                </div>

                <Link
                  to="/login"
                  className="tw-block tw-w-full tw-text-center tw-py-3 tw-px-4 tw-bg-affinicar-primary hover:tw-bg-affinicar-secondary tw-text-white tw-font-semibold tw-rounded-lg tw-transition-colors tw-duration-200"
                >
                  <ArrowLeft className="tw-inline tw-w-4 tw-h-4 tw-mr-2" />
                  Retour à la connexion
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="tw-space-y-6">
                <div>
                  <label htmlFor="email" className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
                    Adresse email
                  </label>
                  <div className="tw-relative">
                    <Mail className="tw-absolute tw-left-3 tw-top-1/2 tw-transform tw--translate-y-1/2 tw-w-5 tw-h-5 tw-text-gray-400" />
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="tw-w-full tw-pl-10 tw-pr-4 tw-py-3 tw-border tw-border-gray-300 tw-rounded-lg focus:tw-ring-2 focus:tw-ring-affinicar-primary focus:tw-border-transparent tw-transition-all"
                      placeholder="votre.email@exemple.com"
                      required
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="tw-w-full tw-py-3 tw-px-4 tw-bg-affinicar-primary hover:tw-bg-affinicar-secondary tw-text-white tw-font-semibold tw-rounded-lg tw-transition-colors tw-duration-200 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-flex tw-items-center tw-justify-center"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="tw-animate-spin tw-w-5 tw-h-5 tw-mr-2" />
                      Envoi en cours...
                    </>
                  ) : (
                    'Envoyer le lien de réinitialisation'
                  )}
                </button>

                <div className="tw-text-center">
                  <Link
                    to="/login"
                    className="tw-text-sm tw-text-affinicar-primary hover:tw-text-affinicar-secondary tw-font-medium tw-inline-flex tw-items-center"
                  >
                    <ArrowLeft className="tw-w-4 tw-h-4 tw-mr-1" />
                    Retour à la connexion
                  </Link>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
